import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  type DocumentData,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { ConsoleLogEntry } from './consoleLogBuffer';
import { capLogEntries } from './consoleLogBuffer';
import { db, storage } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import type {
  CreateCrmFeedbackInput,
  CrmFeedback,
  CrmFeedbackAdminPatch,
  CrmFeedbackStatus,
  CrmFeedbackType,
  CrmFeedbackViewport,
} from '../types/crmFeedback';
import type { UserRole } from '../types';

export const CRM_FEEDBACK_COLLECTION = 'crmFeedback';
export const CRM_FEEDBACK_STORAGE_PREFIX = 'crm-feedback';
export const CRM_FEEDBACK_COUNTER_DOC = 'crm_feedback';

type FsRecord = Record<string, unknown>;

export function buildCrmFeedbackPayload(input: CreateCrmFeedbackInput): FsRecord {
  const now = Timestamp.now();
  const includeLogs = input.type === 'bug' || input.logs.length > 0;
  return {
    type: input.type,
    status: 'open' as CrmFeedbackStatus,
    description: input.description.trim(),
    pageUrl: input.pageUrl,
    userAgent: input.userAgent,
    viewport: input.viewport,
    screenshotUrl: null,
    screenshotPath: null,
    logs: includeLogs ? capLogEntries(input.logs) : [],
    reporterId: input.reporterId,
    reporterName: input.reporterName,
    reporterEmail: input.reporterEmail,
    reporterRole: input.reporterRole,
    adminNote: '',
    number: null,
    createdAt: now,
    updatedAt: now,
  };
}

export function nextCrmBugNumber(current: unknown): number {
  return typeof current === 'number' && Number.isFinite(current) && current >= 1
    ? Math.floor(current)
    : 1;
}

export function formatCrmBugLabel(number: number | null | undefined): string {
  return typeof number === 'number' && number > 0 ? `#${number}` : '';
}

export async function allocateCrmBugNumber(): Promise<number> {
  const counterRef = doc(db, 'settings', CRM_FEEDBACK_COUNTER_DOC);
  return runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const assigned = nextCrmBugNumber(snap.exists() ? snap.data()?.nextNumber : undefined);
    tx.set(counterRef, { nextNumber: assigned + 1, updatedAt: Timestamp.now() }, { merge: true });
    return assigned;
  });
}

/** Assign a public # to an older report that was filed before numbering existed. */
export async function ensureCrmFeedbackNumber(id: string): Promise<number | null> {
  const reportRef = doc(db, CRM_FEEDBACK_COLLECTION, id);
  const counterRef = doc(db, 'settings', CRM_FEEDBACK_COUNTER_DOC);
  return runTransaction(db, async tx => {
    const snap = await tx.get(reportRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    if (typeof data.number === 'number' && data.number > 0) return data.number;
    const counterSnap = await tx.get(counterRef);
    const assigned = nextCrmBugNumber(counterSnap.exists() ? counterSnap.data()?.nextNumber : undefined);
    tx.set(counterRef, { nextNumber: assigned + 1, updatedAt: Timestamp.now() }, { merge: true });
    tx.update(reportRef, { number: assigned, updatedAt: Timestamp.now() });
    return assigned;
  });
}

function asViewport(value: unknown): CrmFeedbackViewport {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    w: typeof raw.w === 'number' ? raw.w : 0,
    h: typeof raw.h === 'number' ? raw.h : 0,
  };
}

function asLogs(value: unknown): ConsoleLogEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(item => ({
      t: typeof item.t === 'string' ? item.t : '',
      level: item.level === 'info' || item.level === 'warn' || item.level === 'error' ? item.level : 'log',
      message: typeof item.message === 'string' ? item.message : '',
    }));
}

export function fromCrmFeedbackDoc(id: string, data: FsRecord): CrmFeedback {
  return {
    id,
    type: data.type === 'feature' ? 'feature' : 'bug',
    status: data.status === 'done' ? 'done' : 'open',
    description: (data.description as string) || '',
    pageUrl: (data.pageUrl as string) || '',
    userAgent: (data.userAgent as string) || '',
    viewport: asViewport(data.viewport),
    screenshotUrl: (data.screenshotUrl as string | null) ?? null,
    screenshotPath: (data.screenshotPath as string | null) ?? null,
    logs: asLogs(data.logs),
    reporterId: (data.reporterId as string) || '',
    reporterName: (data.reporterName as string) || '',
    reporterEmail: (data.reporterEmail as string) || '',
    reporterRole: (data.reporterRole as UserRole | '') || '',
    adminNote: (data.adminNote as string) || '',
    number: typeof data.number === 'number' && data.number > 0 ? data.number : null,
    createdAt: (data.createdAt as Timestamp) ?? Timestamp.now(),
    updatedAt: (data.updatedAt as Timestamp) ?? Timestamp.now(),
  };
}

async function uploadFeedbackScreenshot(
  reporterId: string,
  reportId: string,
  blob: Blob,
): Promise<{ url: string; path: string }> {
  const path = `${CRM_FEEDBACK_STORAGE_PREFIX}/${reporterId}/${reportId}.jpg`;
  const storageRef = ref(storage, path);
  // Force claim refresh so Storage rules can authorize via JWT without a Firestore role read.
  await ensureCrmStorageAccessBeforeUpload(true);
  // Always jpeg for the `.jpg` object name — some canvas blobs report empty/`image/jpg`.
  const contentType =
    blob.type === 'image/png' || blob.type === 'image/webp' ? blob.type : 'image/jpeg';
  await uploadBytes(storageRef, blob, { contentType });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

export async function createCrmFeedback(input: CreateCrmFeedbackInput): Promise<string> {
  const reportRef = doc(collection(db, CRM_FEEDBACK_COLLECTION));
  const payload = buildCrmFeedbackPayload(input);

  try {
    payload.number = await allocateCrmBugNumber();
  } catch (err) {
    console.warn('[crmFeedback] Could not allocate feedback number', err);
  }

  if (input.screenshotBlob && input.screenshotBlob.size > 0) {
    try {
      const uploaded = await uploadFeedbackScreenshot(input.reporterId, reportRef.id, input.screenshotBlob);
      payload.screenshotUrl = uploaded.url;
      payload.screenshotPath = uploaded.path;
    } catch (err) {
      console.warn('[crmFeedback] Screenshot upload failed; submitting without image', err);
    }
  }

  await setDoc(reportRef, payload);
  return reportRef.id;
}

export function subscribeCrmFeedback(
  cb: (items: CrmFeedback[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(db, CRM_FEEDBACK_COLLECTION), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => fromCrmFeedbackDoc(d.id, d.data()))),
    err => {
      firestoreSnapshotListenError('crmFeedbackService', CRM_FEEDBACK_COLLECTION, err);
      onError?.(err);
    },
  );
}

/**
 * Formerly created a userNotifications `bug_squashed` doc (and popup) when an
 * open bug was marked done. Staff asked for quiet closes (#18) — keep Mark done
 * working, but never notify.
 */
export function shouldNotifyBugSquashed(
  _prev: { type?: string; status?: string; reporterId?: string },
  _patch: CrmFeedbackAdminPatch,
): boolean {
  return false;
}

export const BUG_SQUASHED_THANKS =
  'Your bug has been squashed. Your feedback has helped me improve the CRM for everybody. Thank you.';

const MAX_SQUASH_NOTE_CHARS = 240;

export function buildBugSquashedMessage(_description: string, adminNote?: string): string {
  const note = (adminNote || '').trim().replace(/\s+/g, ' ');
  if (note) {
    const shortNote = note.length > MAX_SQUASH_NOTE_CHARS
      ? `${note.slice(0, MAX_SQUASH_NOTE_CHARS - 1)}…`
      : note;
    return `${BUG_SQUASHED_THANKS} ${shortNote}`;
  }
  return BUG_SQUASHED_THANKS;
}

export interface BugSquashedThanksItem {
  notificationId: string;
  feedbackId: string;
  number: number | null;
  adminNote: string;
  message: string;
}

export function subscribeBugSquashedThanks(
  userId: string,
  cb: (items: BugSquashedThanksItem[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(db, 'userNotifications'), where('userId', '==', userId));
  return onSnapshot(
    q,
    snap => {
      const items = snap.docs
        .map(d => {
          const data = d.data();
          if (data.type !== 'bug_squashed') return null;
          const feedbackId = typeof data.sourceId === 'string' ? data.sourceId : d.id;
          return {
            notificationId: d.id,
            feedbackId,
            number: typeof data.number === 'number' ? data.number : null,
            adminNote: typeof data.adminNote === 'string' ? data.adminNote : '',
            message: typeof data.message === 'string' ? data.message : BUG_SQUASHED_THANKS,
          } satisfies BugSquashedThanksItem;
        })
        .filter((item): item is BugSquashedThanksItem => !!item);
      cb(items);
    },
    err => {
      firestoreSnapshotListenError('crmFeedbackService', 'userNotifications', err);
      onError?.(err);
    },
  );
}

export async function markBugSquashThanksSeen(userId: string, feedbackId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId), {
    seenBugSquashIds: arrayUnion(feedbackId),
  });
}

async function notifyBugSquashed(params: {
  reporterId: string;
  feedbackId: string;
  description: string;
  adminNote?: string;
  number?: number | null;
}): Promise<void> {
  try {
    await addDoc(collection(db, 'userNotifications'), {
      userId: params.reporterId,
      type: 'bug_squashed',
      title: 'Bug squashed',
      message: buildBugSquashedMessage(params.description, params.adminNote),
      adminNote: params.adminNote || '',
      number: typeof params.number === 'number' ? params.number : null,
      read: false,
      isPriority: false,
      sourceId: params.feedbackId,
      createdAt: Timestamp.now(),
    });
  } catch (err) {
    console.error('[crmFeedback] Failed to send bug-squashed notification', err);
  }
}

export async function updateCrmFeedback(id: string, patch: CrmFeedbackAdminPatch): Promise<void> {
  const ref = doc(db, CRM_FEEDBACK_COLLECTION, id);
  const prevSnap = await getDoc(ref);
  const prev = prevSnap.exists() ? prevSnap.data() : {};

  const next: FsRecord = { updatedAt: Timestamp.now() };
  if (patch.status !== undefined) next.status = patch.status;
  if (patch.adminNote !== undefined) next.adminNote = patch.adminNote;
  await updateDoc(ref, next as DocumentData);

  if (shouldNotifyBugSquashed(prev, patch)) {
    const savedNote = typeof patch.adminNote === 'string'
      ? patch.adminNote
      : typeof prev.adminNote === 'string' ? prev.adminNote : '';
    await notifyBugSquashed({
      reporterId: String(prev.reporterId || ''),
      feedbackId: id,
      description: typeof prev.description === 'string' ? prev.description : '',
      adminNote: savedNote,
      number: typeof prev.number === 'number' ? prev.number : null,
    });
  }
}

export function isCrmFeedbackType(value: string): value is CrmFeedbackType {
  return value === 'bug' || value === 'feature';
}
